/**
 * Tailwind CSS implementation of FormField.
 * Uses the Seeds form-field CSS class from form-field.css. Only FormField's own
 * wrapper becomes a plain <div className="seeds-form-field"> — the Label, helper
 * Text, error Text, VisuallyHidden, and children render-prop are composed exactly
 * as in the styled path so the accessibility/qa contract is preserved.
 */

import React, { useMemo, useState } from "react";
import { useTextContent } from "@sproutsocial/seeds-react-hooks";
import Label from "@sproutsocial/seeds-react-label";
import Text from "@sproutsocial/seeds-react-text";
import { VisuallyHidden } from "@sproutsocial/seeds-react-visually-hidden";
import type { TypeFormFieldProps } from "./FormFieldTypes";
import { nextFormFieldId } from "./formFieldId";

// Utility for merging class names properly
function cn(
  ...inputs: (string | undefined | null | false | Record<string, boolean>)[]
): string {
  const classes: string[] = [];

  for (const input of inputs) {
    if (!input) continue;

    if (typeof input === "string") {
      classes.push(input);
    } else if (typeof input === "object") {
      for (const [key, value] of Object.entries(input)) {
        if (value) {
          classes.push(key);
        }
      }
    }
  }

  return classes.join(" ");
}

export const FormFieldTailwind = React.forwardRef<
  HTMLDivElement,
  TypeFormFieldProps
>((props, ref) => {
  const {
    children,
    error,
    helperText,
    id: identifier,
    isInvalid = false,
    label,
    mb = 400,
    qa,
    isLabelHidden = false,
    required,
    className,
    style,
    ...rest
  } = props as TypeFormFieldProps & {
    className?: string;
    style?: React.CSSProperties;
  };

  const [id] = useState(identifier || nextFormFieldId());
  const errorId = `Error-${id}`;
  const helperTextId = `HelperText-${id}`;
  const containerText = useTextContent("");
  const errorContainerText = useTextContent("");

  const ariaDescribedby = useMemo(() => {
    const ids = [
      helperText && helperTextId,
      isInvalid && error && errorId,
    ].filter(Boolean);
    return ids.length > 0 ? ids.join(" ") : undefined;
  }, [helperText, helperTextId, isInvalid, error, errorId]);

  return (
    <div
      ref={ref}
      className={cn("seeds-form-field", className)}
      // `rest` is typed with the full styled-system Box prop surface (via
      // TypeFormFieldProps extends Omit<TypeBoxProps, "children">), but the
      // Hybrid routes any instance carrying styled-system props to the styled
      // path, so on the Tailwind path `rest` only ever holds native div
      // attributes at runtime. Widen the spread to the div's attribute type.
      {...(rest as unknown as React.HTMLAttributes<HTMLDivElement>)}
      {...qa}
      // FormField's only own style is the bottom margin; map the space token
      // inline so scalar mb matches the styled path (responsive mb is routed to
      // the styled path by FormFieldHybrid).
      style={{ marginBottom: `var(--space-${String(mb)})`, ...style }}
      data-qa-formfield={
        (qa && qa["data-qa-formfield"]) || id || containerText.current
      }
      data-qa-formfield-isinvalid={isInvalid === true}
    >
      {isLabelHidden ? (
        <VisuallyHidden data-testid="visually-hidden">
          <Label htmlFor={id} required={required}>
            {label}
          </Label>
        </VisuallyHidden>
      ) : (
        <Label mb={helperText ? 100 : 300} htmlFor={id} required={required}>
          {label}
        </Label>
      )}
      {helperText && (
        <Text
          as="p"
          fontSize={200}
          mb={300}
          color="text.subtext"
          id={helperTextId}
        >
          {helperText}
        </Text>
      )}
      {children({
        id,
        isInvalid,
        ariaDescribedby,
        ...(required !== undefined && { required }),
      })}
      {isInvalid && error && (
        <Text
          as="div"
          fontSize={200}
          color="text.error"
          mt={300}
          id={errorId}
          data-qa-formfield-error={
            (qa && qa["data-qa-formfield-error"]) || errorContainerText.current
          }
        >
          {error}
        </Text>
      )}
    </div>
  );
});

FormFieldTailwind.displayName = "FormFieldTailwind";
